/**
 * useAgents — polls the agents endpoint and exposes per-agent controls.
 *
 * Polls GET /api/admin/ops/agents every 15 s.
 *
 * Returns:
 *   agents           — full list (platform + legal combined)
 *   platformAgents   — filtered to kind === "platform"
 *   legalAgents      — filtered to kind === "legal"
 *   total24hCost     — total_24h_cost_usd from the last response
 *   loading          — true only during the first in-flight fetch
 *   error            — last axios error, or null
 *   refetch          — force an immediate refresh
 *
 * Actions (all return Promises, call refetch() on success):
 *   pauseAgent(agentId, reason)   → POST /api/admin/ops/agents/:id/pause
 *   resumeAgent(agentId)          → POST /api/admin/ops/agents/:id/resume
 *   killAgent(agentId, reason)    → POST /api/admin/ops/agents/:id/kill
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

const POLL_INTERVAL_MS = 15_000;

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Encode an agent_id for use in a URL path segment.
 * Handles IDs that contain "/" (e.g. "legal/nda_agent").
 */
function encodeAgentId(agentId) {
  return agentId.split("/").map(encodeURIComponent).join("/");
}

export function useAgents() {
  const [agents, setAgents]           = useState([]);
  const [total24hCost, setTotal24hCost] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const intervalRef = useRef(null);

  // ── Core fetch ──────────────────────────────────────────────────────────────

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/agents`, {
        headers: authHeaders(),
      });
      setAgents(res.data?.agents || []);
      setTotal24hCost(res.data?.total_24h_cost_usd ?? null);
      setError(null);
    } catch (err) {
      setError(err);
      // Retain stale data — do NOT wipe the agents list on a transient failure.
    } finally {
      // Only relevant on the first load; subsequent polls silently update state.
      setLoading(false);
    }
  }, []); // stable — no dependencies

  // ── Polling setup + teardown ────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    fetchAgents();

    intervalRef.current = setInterval(fetchAgents, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchAgents]);

  // ── Derived lists ───────────────────────────────────────────────────────────

  const platformAgents = agents.filter((a) => a.kind === "platform");
  const legalAgents    = agents.filter((a) => a.kind === "legal");

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * POST /api/admin/ops/agents/:id/pause
   * payload: { reason }
   */
  const pauseAgent = useCallback(async (agentId, reason) => {
    const res = await axios.post(
      `${API}/admin/ops/agents/${encodeAgentId(agentId)}/pause`,
      { reason },
      { headers: authHeaders() },
    );
    await fetchAgents();
    return res.data;
  }, [fetchAgents]);

  /**
   * POST /api/admin/ops/agents/:id/resume
   */
  const resumeAgent = useCallback(async (agentId) => {
    const res = await axios.post(
      `${API}/admin/ops/agents/${encodeAgentId(agentId)}/resume`,
      {},
      { headers: authHeaders() },
    );
    await fetchAgents();
    return res.data;
  }, [fetchAgents]);

  /**
   * POST /api/admin/ops/agents/:id/kill
   * payload: { reason, confirmation: "kill <agentId>" }
   */
  const killAgent = useCallback(async (agentId, reason) => {
    const res = await axios.post(
      `${API}/admin/ops/agents/${encodeAgentId(agentId)}/kill`,
      { reason, confirmation: `kill ${agentId}` },
      { headers: authHeaders() },
    );
    await fetchAgents();
    return res.data;
  }, [fetchAgents]);

  // ── Public API ──────────────────────────────────────────────────────────────

  return {
    agents,
    platformAgents,
    legalAgents,
    total24hCost,
    loading,
    error,
    refetch: fetchAgents,
    pauseAgent,
    resumeAgent,
    killAgent,
  };
}
